import * as React from "react"
import { cloneElement, PropsWithChildren, ReactElement, useState } from "react"
import styled from "styled-components"
import { colors, scale } from "../../styles"
import { Flex } from "../Flex"
import { INNER_MAX_WIDTH } from "../../constants"

export interface TabComponents {
	Tab: React.FC<TabProps>
}

export interface TabsProps {
	children: React.ReactNode
	defaultActiveTabId?: string
}

const Tabs: React.FC<TabsProps> & TabComponents = ({
	children,
	defaultActiveTabId,
}) => {
	const [activeTab, setActiveTab] = useState(defaultActiveTabId)
	return (
		<Flex flexDirection={"column"} w={1}>
			<Flex
				flexDirection={"row"}
				mb={{ _: 2, m: 5 }}
				role="tablist"
				aria-label={"tabs"}
				backgroundColor="blue11"
			>
				<Flex
					w={1}
					maxw={INNER_MAX_WIDTH}
					mx="auto"
					py={{ _: 2, m: 4 }}
					px={{ _: 3, xl: 0 }}
				>
					{React.Children.map(children, (child, index) => {
						const item = child as ReactElement<PropsWithChildren<TabProps>>
						const id = item.props.id
						const isActive = id === activeTab
						const onClick = () => {
							setActiveTab(id)
							item.props.onClick?.()
						}
						return (
							<Flex
								ml={index !== 0 ? scale.px(4) : 0}
								as="button"
								role="tab"
								aria-selected={isActive ? "true" : "false"}
								aria-controls={`panel-${id}`}
								onClick={onClick}
								id={`tab-${id}`}
							>
								{cloneElement<TabProps>(item, { isActive })}
							</Flex>
						)
					})}
				</Flex>
			</Flex>
			{React.Children.map(children, (child) => {
				const item = child as ReactElement<PropsWithChildren<TabProps>>
				const isActive = item.props.id === activeTab
				return (
					<div
						id={`panel-${item.props.id}`}
						role="tabpanel"
						aria-labelledby={`tab-${item.props.id}`}
						tabIndex={0}
						hidden={!isActive}
					>
						{item.props.children}
					</div>
				)
			})}
		</Flex>
	)
}

interface TabProps {
	id: string
	isActive?: boolean
	onClick?: () => void
	title: string
	children?: React.ReactNode
}

const StyledTabFlex = styled(Flex) <{ active: boolean }>`
	border-bottom: ${({ active }) =>
		active ? `2px solid ${colors.navy}` : "none"};
`

const Tab: React.FC<TabProps> = ({ isActive = false, onClick, title }) => {
	return (
		<StyledTabFlex px={2} pb={1} active={isActive} onClick={onClick}>
			{title}
		</StyledTabFlex>
	)
}

Tabs.Tab = Tab

export { Tabs }
